import React, { useState, useRef, useEffect } from "react";
import { RuleType } from "winslow-core";
import "./ConditionCard.css";

interface BaseInputProps {
  value: string;
  onChange: (value: string) => void;
}

export const NumberInput: React.FC<BaseInputProps> = ({ value, onChange }) => {
  return (
    <input
      type="number"
      className="condition-card-value-input"
      placeholder="Enter number..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Number value input"
    />
  );
};

interface DateRangeInputProps extends BaseInputProps {
  rule: RuleType | string;
}

export const DateRangeInput: React.FC<DateRangeInputProps> = ({
  value,
  onChange,
  rule,
}) => {
  const isRangeRule =
    rule === RuleType.BETWEEN || rule === RuleType.NOT_BETWEEN;

  // Parse value - expects array of dates for BETWEEN/NOT_BETWEEN
  const parseDateValue = (): [string, string] => {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed) && parsed.length === 2) {
        return [parsed[0] || "", parsed[1] || ""];
      }
    } catch {
      // Not JSON or invalid format
    }
    return ["", ""];
  };

  const [startDate, endDate] = parseDateValue();

  const handleStartDateChange = (newStartDate: string) => {
    onChange(JSON.stringify([newStartDate, endDate]));
  };

  const handleEndDateChange = (newEndDate: string) => {
    onChange(JSON.stringify([startDate, newEndDate]));
  };

  // Only render date range picker for BETWEEN/NOT_BETWEEN rules
  if (isRangeRule) {
    return (
      <div style={{ display: "flex", gap: "8px", width: "100%" }}>
        <input
          type="date"
          className="condition-card-value-input"
          value={startDate}
          onChange={(e) => handleStartDateChange(e.target.value)}
          placeholder="Start date"
          aria-label="Start date"
          style={{ flex: 1 }}
        />
        <span style={{ alignSelf: "center", color: "#666" }}>to</span>
        <input
          type="date"
          className="condition-card-value-input"
          value={endDate}
          onChange={(e) => handleEndDateChange(e.target.value)}
          placeholder="End date"
          aria-label="End date"
          style={{ flex: 1 }}
        />
      </div>
    );
  }

  // No other rules supported for dates
  return (
    <input
      type="text"
      className="condition-card-value-input"
      placeholder="Select 'Between' or 'Not Between' rule for date range"
      value=""
      readOnly
      aria-label="Date range placeholder"
    />
  );
};

export const CheckboxSingleInput: React.FC<BaseInputProps> = ({
  value,
  onChange,
}) => {
  const isChecked = value === "true";

  return (
    <div style={{ display: "flex", gap: "16px", padding: "8px 0" }}>
      <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <input
          type="radio"
          name="checkbox-single"
          checked={isChecked}
          onChange={() => onChange("true")}
        />
        <span>Checked</span>
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <input
          type="radio"
          name="checkbox-single"
          checked={!isChecked}
          onChange={() => onChange("false")}
        />
        <span>Unchecked</span>
      </label>
    </div>
  );
};

interface ChoiceInputProps extends BaseInputProps {
  choices: string[];
}

export const CheckboxMultipleInput: React.FC<ChoiceInputProps> = ({
  value,
  onChange,
  choices,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse selected values from JSON array
  const selectedValues: string[] = (() => {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  const toggleChoice = (choice: string) => {
    const newValues = selectedValues.includes(choice)
      ? selectedValues.filter((v) => v !== choice)
      : [...selectedValues, choice];
    onChange(JSON.stringify(newValues));
  };

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      style={{ position: "relative", width: "100%" }}
      className="condition-card-value-container">
      <div
        className="condition-card-value-input"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        <span>
          {selectedValues.length > 0
            ? `${selectedValues.length} selected`
            : "Select values..."}
        </span>
        <span>▼</span>
      </div>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ddd",
            borderTop: "none",
            borderRadius: "0 0 4px 4px",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 1000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}>
          {choices.map((choice) => (
            <label
              key={choice}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "8px 12px",
                cursor: "pointer",
                borderBottom: "1px solid #eee",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
              }}>
              <input
                type="checkbox"
                checked={selectedValues.includes(choice)}
                onChange={() => toggleChoice(choice)}
                style={{ marginRight: "8px" }}
              />
              <span>{choice}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export const RadioInput: React.FC<ChoiceInputProps> = ({
  value,
  onChange,
  choices,
}) => {
  return (
    <select
      className="condition-card-value-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Radio value select">
      <option value="">Select value...</option>
      {choices.map((choice) => (
        <option key={choice} value={choice}>
          {choice}
        </option>
      ))}
    </select>
  );
};

interface DropdownInputProps extends ChoiceInputProps {
  editable?: boolean;
  suggestions?: string[];
}

export const DropdownInput: React.FC<DropdownInputProps> = ({
  value,
  onChange,
  choices,
  editable = false,
  suggestions = [],
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Combine predefined choices with suggestions
  const allOptions = Array.from(
    new Set([...choices, ...suggestions.filter((s) => s)])
  );

  // Filter options based on input
  const filteredOptions = allOptions.filter((option) =>
    option.toLowerCase().includes(inputValue.toLowerCase())
  );

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  if (editable) {
    return (
      <div style={{ position: "relative", width: "100%" }}>
        <input
          type="text"
          className="condition-card-value-input"
          placeholder="Enter or select value..."
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange(e.target.value);
          }}
          onFocus={() => setShowSuggestions(true)}
          aria-label="Dropdown value input"
        />
        {showSuggestions && filteredOptions.length > 0 && (
          <div
            ref={suggestionsRef}
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderTop: "none",
              borderRadius: "0 0 4px 4px",
              maxHeight: "200px",
              overflowY: "auto",
              zIndex: 1000,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}>
            {filteredOptions.map((option, idx) => (
              <div
                key={idx}
                onClick={() => {
                  onChange(option);
                  setInputValue(option);
                  setShowSuggestions(false);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom:
                    idx < filteredOptions.length - 1 ? "1px solid #eee" : "none",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}>
                {option}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <select
      className="condition-card-value-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Dropdown value select">
      <option value="">Select value...</option>
      {choices.map((choice) => (
        <option key={choice} value={choice}>
          {choice}
        </option>
      ))}
    </select>
  );
};

interface TextInputWithSuggestionsProps extends BaseInputProps {
  suggestions?: string[];
  loadingSuggestions?: boolean;
  onFocus?: () => void;
}

export const TextInputWithSuggestions: React.FC<
  TextInputWithSuggestionsProps
> = ({ value, onChange, suggestions = [], loadingSuggestions = false, onFocus }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  const handleFocus = () => {
    setShowSuggestions(true);
    if (onFocus) {
      onFocus();
    }
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        className="condition-card-value-input"
        placeholder="Enter value..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        aria-label="Value input"
      />
      {showSuggestions && (
        <div
          ref={suggestionsRef}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ddd",
            borderTop: "none",
            borderRadius: "0 0 4px 4px",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 1000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}>
          {loadingSuggestions ? (
            <div
              style={{
                padding: "8px 12px",
                color: "#666",
                fontStyle: "italic",
              }}>
              Loading suggestions...
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((suggestion, idx) => (
              <div
                key={idx}
                onClick={() => {
                  onChange(suggestion);
                  setShowSuggestions(false);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom:
                    idx < suggestions.length - 1 ? "1px solid #eee" : "none",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}>
                {suggestion}
              </div>
            ))
          ) : value.trim() ? (
            <div
              style={{
                padding: "8px 12px",
                color: "#666",
                fontStyle: "italic",
              }}>
              No suggestions found
            </div>
          ) : (
            <div
              style={{
                padding: "8px 12px",
                color: "#666",
                fontStyle: "italic",
              }}>
              Type to search for values...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

